// Prompt AR (2026-09-25): herramientas y maquinaria DEL CLIENTE que ingresan al
// objetivo para trabajar y después se retiran. El vigilador registra el ingreso
// y la salida; admin y supervisor ven el historial por objetivo y sede.
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { SiteSelect } from "@/components/SiteSelect";
import { AttachmentPicker, AttachmentGrid } from "@/components/AttachmentPicker";
import { Wrench, LogIn, LogOut } from "lucide-react";

const EMPTY = { description: "", quantity: 1, owner: "", brought_by: "", brought_by_dni: "", notes: "" };

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" }) : "");

export function ClientToolsLog({ role }) {
  const isAdmin = role === "admin";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [status, setStatus] = useState("adentro");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exitFor, setExitFor] = useState(null);
  const [exitNote, setExitNote] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/clients");
      setClients(data || []);
      if ((data || []).length === 1) setClientId(data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  // Igual que en Visitas y en el Check list: la sede se completa sola con la sede
  // donde está trabajando hoy (ronda o turno). Se puede cambiar a mano.
  useEffect(() => {
    setSiteId("");
    if (!clientId || isAdmin) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/materials/my-site`);
        if (!cancel && data?.site_id) setSiteId((s) => s || data.site_id);
      } catch { /* sin sede deducida */ }
    })();
    return () => { cancel = true; };
  }, [clientId, isAdmin]);

  const load = async () => {
    try {
      const params = { status };
      if (clientId) params.client_id = clientId;
      if (siteId) params.site_id = siteId;
      const { data } = await api.get("/client-tools", { params });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, siteId, status]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitEntry = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!clientId) { toast.error("Elegí el objetivo"); return; }
    setBusy(true);
    try {
      await api.post("/client-tools", {
        client_id: clientId, site_id: siteId || null,
        description: form.description, quantity: Number(form.quantity) || 1,
        owner: form.owner || null, brought_by: form.brought_by || null,
        brought_by_dni: form.brought_by_dni || null, notes: form.notes || null,
        attachments: photos,
      });
      toast.success("Ingreso registrado");
      setForm(EMPTY); setPhotos([]);
      if (status !== "adentro") setStatus("adentro"); else load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitExit = async (row) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/client-tools/${row.id}/exit`, { notes: exitNote || null, attachments: [] });
      toast.success("Salida registrada");
      setExitFor(null); setExitNote("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ marginTop: 18 }} data-testid="client-tools-section">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Wrench size={18} style={{ color: "#BF3437" }} />
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: "#0A0A0A" }}>Herramientas del cliente</h2>
      </div>
      <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 12 }}>
        Maquinaria y herramientas propias del cliente o de sus contratistas que ingresan a trabajar y después se retiran.
      </div>

      <div className="field">
        <label>Objetivo *</label>
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="client-tools-client">
          <option value="">{isAdmin || role === "supervisor" ? "Todos los objetivos" : "Elegí el objetivo..."}</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <SiteSelect clientId={clientId} value={siteId} onChange={setSiteId} testIdPrefix="client-tools" />

      {clientId && (
        <form onSubmit={submitEntry} style={{ borderTop: "1px solid var(--invict-bone-3)", paddingTop: 12, marginTop: 4 }} data-testid="client-tools-entry-form">
          <div style={{ fontSize: 12, fontWeight: 800, color: "#0A0A0A", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <LogIn size={14} /> Registrar ingreso
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 90px", gap: 8 }}>
            <div className="field"><label>Qué ingresa *</label>
              <input required minLength={2} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Ej: Hormigonera, amoladora, andamio" data-testid="client-tools-description" />
            </div>
            <div className="field"><label>Cantidad</label>
              <input type="number" min={1} max={999} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="client-tools-quantity" />
            </div>
          </div>
          <div className="field"><label>De quién es (empresa / contratista)</label>
            <input value={form.owner} onChange={(e) => set("owner", e.target.value)} data-testid="client-tools-owner" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: 8 }}>
            <div className="field"><label>Quién la ingresa</label>
              <input value={form.brought_by} onChange={(e) => set("brought_by", e.target.value)} data-testid="client-tools-brought-by" />
            </div>
            <div className="field"><label>DNI</label>
              <input inputMode="numeric" value={form.brought_by_dni} onChange={(e) => set("brought_by_dni", e.target.value)} data-testid="client-tools-dni" />
            </div>
          </div>
          <div className="field"><label>Observaciones</label>
            <input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="client-tools-notes" />
          </div>
          <AttachmentPicker value={photos} onChange={setPhotos} accept="image" max={3} label="Foto (opcional)" />
          <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 10, marginTop: 8 }} data-testid="client-tools-entry-submit">
            {busy ? "Guardando..." : "Registrar ingreso"}
          </button>
        </form>
      )}

      <div style={{ display: "flex", gap: 6, margin: "16px 0 10px" }}>
        {[["adentro", "En el objetivo"], ["retirado", "Retiradas"], ["todos", "Todas"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`client-tools-filter-${k}`}
            style={{ padding: "6px 12px", fontSize: 11, fontWeight: 800, borderRadius: 4, border: "1px solid var(--invict-bone-3)", cursor: "pointer",
              background: status === k ? "#0A0A0A" : "#fff", color: status === k ? "#FFFBF4" : "#0A0A0A" }}>
            {label}
          </button>
        ))}
      </div>

      {rows.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }} data-testid="client-tools-empty">No hay registros.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((r) => (
          <div key={r.id} style={{ border: "1px solid var(--invict-bone-3)", borderRadius: 8, padding: 10 }} data-testid={`client-tools-row-${r.id}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <b style={{ fontSize: 13, color: "#0A0A0A" }}>{r.quantity > 1 ? `${r.quantity} × ` : ""}{r.description}</b>
              <span className={`badge ${r.status === "adentro" ? "green" : "gray"}`}>{r.status === "adentro" ? "EN EL OBJETIVO" : "RETIRADA"}</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 2 }}>
              {r.client_name}{r.site_name ? ` · ${r.site_name}` : ""}{r.owner ? ` · De: ${r.owner}` : ""}
            </div>
            <div style={{ fontSize: 12, marginTop: 2 }}>
              Ingresó {fmt(r.entered_at)}{r.brought_by ? ` · la trajo ${r.brought_by}${r.brought_by_dni ? ` (DNI ${r.brought_by_dni})` : ""}` : ""} · registró {r.entered_by_name}
            </div>
            {r.notes && <div style={{ fontSize: 11, fontStyle: "italic", color: "var(--invict-text-muted)", marginTop: 2 }}>{r.notes}</div>}
            {r.entry_attachments?.length > 0 && <div style={{ marginTop: 6 }}><AttachmentGrid items={r.entry_attachments} /></div>}
            {r.status === "retirado" && (
              <div style={{ fontSize: 12, marginTop: 4, color: "#0A0A0A" }}>
                Se retiró {fmt(r.exited_at)} · registró {r.exited_by_name}{r.exit_notes ? ` · ${r.exit_notes}` : ""}
              </div>
            )}
            {r.status === "adentro" && (exitFor === r.id ? (
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                <input value={exitNote} onChange={(e) => setExitNote(e.target.value)} placeholder="Observación de salida (opcional)" style={{ flex: 1, minWidth: 160 }} data-testid={`client-tools-exit-note-${r.id}`} />
                <button type="button" disabled={busy} onClick={() => submitExit(r)} className="btn-primary" style={{ padding: "6px 12px", fontSize: 11 }} data-testid={`client-tools-exit-confirm-${r.id}`}>Confirmar salida</button>
                <button type="button" onClick={() => { setExitFor(null); setExitNote(""); }} style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>Cancelar</button>
              </div>
            ) : (
              <button type="button" onClick={() => { setExitFor(r.id); setExitNote(""); }} style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, border: "none", background: "transparent", color: "#BF3437", fontSize: 11, fontWeight: 800, cursor: "pointer" }} data-testid={`client-tools-exit-open-${r.id}`}>
                <LogOut size={12} /> Registrar salida
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ClientToolsLog;
